<template>
  <div class="outer d-flex editor-outer">
    <monaco-editor
      ref="editor"
      :env="env"
      :language="datatype"
      v-if="textarea"
      v-model="editorValue"
      :options="monacoOptions"
      class="editor editor__textarea"
      @change="onChange"
    />
    <monaco-singleline
      v-else
      v-model="editorValue"
      :options="monacoOptions"
      @change="onChange"
      class="editor"
    />
    <v-icon v-if="textarea" class="editor-resizer">mdi-resize-bottom-right</v-icon>
  </div>
</template>

<script>
import MonacoEditor from "vue-monaco";
import MonacoSingleline from "vue-monaco-singleline";

export default {
  name: "Editor",
  props: {
    value: {
      type: [String, Number],
      required: false,
    },
    textarea: {
      type: Boolean,
      default: false,
    },
    env: {},
    conf: {},
    datatype: null,
  },
  components: {
    MonacoEditor,
    MonacoSingleline,
  },
  data() {
    return {
      editorValue: this.value,
      monacoOptions: {
        automaticLayout: true,
        autoIndent: true,
        contextmenu: false,
        formatOnType: true,
        minimap: {
          enabled: false,
        },
        theme: this.vsTheme,
        lineNumbers: false,
      },
      keywords: [],
      completionProvider: null,
      status: false,
      language: this.datatype === 'javascript' ? 'javascript' : 'custom',
    };
  },
  watch: {
    value: {
      handler(newVal) {
        if(newVal !== undefined) {
          this.editorValue = newVal;
        }
      },
      immediate: true,
    }
  },
  computed: {
    isDarkMode() {
      return this.$store.state.mode.darkMode;
    },
    vsTheme() {
      return this.isDarkMode ? "vs-dark" : "vs";
    },
  },
  mounted() {
    this.$root.$on("disposeCompletionProvider", () => {
      try {
        this.completionProvider?.dispose();
      } catch (e) {
        console.log(e);
      }
    });

    this.$root.$on("toggled-interface-expansion", () => {
        window.dispatchEvent(new Event('resize'));
    })

    if (this.env) {
      Object.keys(this.env).forEach((key) => {
        this.keywords.push({
          label: `${key}`,
          kind: window.monaco.languages.CompletionItemKind.Snippet,
          description: `=<.${key}>=`,
          insertText: `<.${key}>=`,
          insertTextRules:
            window.monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        });
      });
      this.setCompletionItems();
    }
  },
  methods: {
    setCompletionItems() {
      this.completionProvider = window.monaco.languages.registerCompletionItemProvider(
        this.datatype,
        {
          triggerCharacters: ["="],
          provideCompletionItems: (model, position) => {
            const wordBeforePosition = model.getWordUntilPosition({
              lineNumber: position.lineNumber,
              column: position.column - 1,
            });

            const wordUntilPosition = model.getWordUntilPosition(position);
            if (
              wordBeforePosition.word.trim() === "" ||
              wordUntilPosition.word.trim() === ""
            ) {
              const keywords = this.keywords;

              const suggestions = keywords.map((id) => ({
                label: id.label,
                kind: id.kind,
                description: id.description,
                documentation: id.description,
                insertText: id.insertText,
                detail: id.description,
                insertTextRules: id.insertTextRules,
                range: {
                  startLineNumber: position.lineNumber,
                  startColumn: wordUntilPosition.startColumn,
                  endLineNumber: position.lineNumber,
                  endColumn: wordUntilPosition.endColumn - 1,
                },
              }));
              return { suggestions };
            }
          },
        }
      );
      this.status = true;
    },
    onChange(value) {
      this.$emit("input", value);
    },
  },
};
</script>

<style lang="scss" scoped>

::v-deep .main {
  width: 90%;
}

::v-deep .editor {
  border: 1px solid var(--apio-shade-4);
  border-radius: 4px;
  width: 100%;
  overflow: hidden;

  &__textarea {
    height: 200px;
  }
}

.editor-outer {
  position: relative;
}

.editor-resizer {
  position: absolute;
  bottom: 0px;
  right: 0px;
}
</style>

<style lang="css">
.monaco-editor .suggest-details, .monaco-editor .suggest-widget {
  border-style: none !important;
}
.editor__textarea {
  resize: vertical;
  overflow: auto;
}
</style>
